import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import PageHeader from "../components/PageHeader";
import AllPicksTable from "../components/AllPicksTable";
import { getAllPicks } from "../api/premierLeague";
import { useAuth } from "../auth/AuthContext";
import { usePlSeason } from "../hooks/usePlSeason";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";

/** Every player's pick for the whole season, on its own page. */
export default function PlAllPicksPage() {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { user, logout } = useAuth();
  const { seasonId: hookSeasonId } = usePlSeason();
  // The pool's own season always wins — an archived pool must not be read
  // against whatever season happens to be current.
  const seasonId = location.state?.seasonId ?? hookSeasonId;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!seasonId) return;
    setLoading(true);
    setError("");
    try {
      setData(await getAllPicks(poolId, seasonId));
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load picks.");
    } finally {
      setLoading(false);
    }
  }, [seasonId, poolId]);

  useEffect(() => {
    load();
  }, [load]);

  const backToLeaderboard = () =>
    navigate(`/premier-league/current/${poolId}`, { state: { seasonId } });

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white" overflowX="hidden">
      <PageHeader
        title="PREMIER LEAGUE ALL PICKS"
        onBack={backToLeaderboard}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={{ base: 5, md: 8 }}>
        <VStack align="stretch" spacing={5}>
          <Flex justify="flex-start">
            <Button size="sm" bg="white" color={TOP_BAR_BLUE}
                    _hover={{ bg: "gray.100" }} onClick={backToLeaderboard}>
              Return to Leaderboard
            </Button>
          </Flex>

          {error && (
            <Alert status="error" borderRadius="md" color="black">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
               borderRadius="lg" boxShadow="lg" p={{ base: 4, md: 6 }}>
            <Flex justify="space-between" align="center" mb={1} flexWrap="wrap" gap={2}>
              <Heading size="md" color={TOP_BAR_BLUE}>
                {data?.pool_name ? `${data.pool_name} — All Picks` : "All Picks"}
              </Heading>
              <HStack spacing={4} fontSize="xs" color="gray.600">
                <Text>Picks stay hidden until that fixture kicks off.</Text>
              </HStack>
            </Flex>
            <Text fontSize="xs" color="gray.500" mb={4}>
              A red outline marks an autopick.
            </Text>

            <AllPicksTable data={data} loading={loading} weekPrefix="MW" />
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
